import moment from 'moment';
import React, { useMemo, useState, useEffect, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { groupBy } from 'lodash';
import { API_DATE_FORMAT, API_DATE_TIME_FORMAT } from '@ubeya/shared/constants';
import {
  FlexMiddle,
  Header2SemiBold,
  Modal as BaseModal,
  NameRenderer,
  Search as BaseSearch,
  SelectHeader,
  SmallBodyBold,
  TimeAgoRenderer,
  PaginationTable,
  ContextMenu as BaseContextMenu
} from '@ubeya/shared-web/components';
import useRequests from '@ubeya/shared/hooks/account/useRequests';
import useLeaveTypes from '@ubeya/shared/hooks/account/useLeaveTypes';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import { responsiveShow } from '@ubeya/shared-web/style';
import useRequestsTypes, {
  RequestTypes,
  RequestTypesSlugs,
  TYPES_COLROS
} from '@ubeya/shared/hooks/generic/useRequestsTypes';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useAdmins from '@ubeya/shared/hooks/account/useAdmins';
import { REQUEST_STATUS, REQUESTS_STATUS } from '@ubeya/shared/constants';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import * as sharedAtoms from '@ubeya/shared/atoms/shared';
import { useRecoilState, useRecoilValue } from 'recoil';
import useDebounce from '@ubeya/shared/hooks/useDebounce';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useCheckbox from '@ubeya/shared-web/hooks/Table/useCheckbox';
import { ReactComponent as BasePremiumStartIcon } from '@ubeya/shared-web/assets/lock.svg';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import { isRequestEligibleForApproval } from 'shared/utils/requests';
import useLeaveRequestApproval from 'shared/hooks/useLeaveRequestApproval';
import LeaveApprovalConfirmModal from '../LeaveApprovalConfirmModal';
import LeaveEarningsLimitWarningModal from '../LeaveEarningsLimitWarningModal';
import EmployeeCard from '../EmployeeCard/EmployeeCard';
import BranchPicker from '../BranchPicker/BranchPicker';
import TypeRenderer from './TypeRenderer';
import WhenRenderer from './WhenRenderer';
import ActionRenderer from './ActionRenderer';
import TypeRendererDetails from './TypeRendererDetails';
import DetailsRenderer from './DetailsRenderer';
import WhenRendererDetails from './WhenRendererDetails';
import EmptyState from './EmptyState';
import SwapSideBar from './SwapSideBar';
import RequestDeclineReasonModal from './RequestDeclineReasonModal';
import NotesRenderer from './NotesRenderer';
import RequestModalStickyBar from './RequestModalStickyBar';
import { formatHoursDaysRequested } from './HoursDaysRequestedFormatter';
import RequestsFilterBanner from './RequestsFilterBanner';

const Modal = styled(BaseModal)`
  > .content {
    padding: 16px;
  }
`;

const TitleRow = styled(FlexMiddle)`
  > * {
    margin-right: 20rem;
  }
`;
const ContextMenu = styled(BaseContextMenu)`
  margin-bottom: 8px;
`;

const Types = styled(FlexMiddle)`
  gap: 20rem;
  margin-bottom: 8px;
  ${responsiveShow.tablet}
`;

const SubHeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const Search = styled(BaseSearch)`
  margin-right: 10px;

  > div:last-child > input {
    padding: 5px 10px;
    font-size: 16px;
  }
`;

const BranchPickerContainer = styled.div`
  padding-left: 10px;
`;

const BookerSelectContainer = styled.div`
  margin-right: 10rem;
`;

const Dot = styled.div`
  width: 10rem;
  height: 10rem;
  background: ${({ color }) => color};
  border-radius: 100%;
  margin-right: 8rem;
`;

const HistorySelect = styled(FlexMiddle)`
  flex: 1;
  justify-content: flex-end;
`;

const Table = styled(PaginationTable)`
  .row.header-row.open {
    border-bottom: none;
  }

  .row.sub-row {
    cursor: default;
  }

  .row.group {
    background-color: ${({ theme }) => theme.colors.white} !important;
    &:hover {
      background-color: ${({ theme }) => theme.colors.hoverBackground} !important;
    }
  }
`;

const PremiumStartIcon = styled(BasePremiumStartIcon)`
  margin-left: 5px;
  fill: ${({ theme }) => theme.colors.primary};
`;

// sentinel for the "All bookers" option — must not be null, since the Select's
// default filter excludes null-valued options from search results
const ALL_BOOKERS = 'all';

const RequestsModal = ({ onClose }) => {
  const {
    t,
    shortDateFormat,
    timeFormat,
    enableContractFeature,
    enableContractLeaveManagementFeature,
    isAccountNetworkAccessSupplier,
    organizationId
  } = useContext(ConfigContext);
  const myPrivileges = useContext(PrivilegesContext);
  const { isLoading: loadingRequestsType, mappedRequestsTypes, mappedRequestsTypesBySlug } = useRequestsTypes();

  const { mappedBranches } = useBranches();
  const { accountId } = useAccount();
  const { mappedLabels } = useLabels();
  const { mappedLocations } = useLocations();
  const { mappedPositions } = usePositions();
  const { leaveTypes } = useLeaveTypes();
  const mappedLeaveTypes = useMemo(
    () => (leaveTypes || []).reduce((acc, leaveType) => ({ ...acc, [leaveType.id]: leaveType }), {}),
    [leaveTypes]
  );

  const [search, setSearch] = useState('');
  const [isBulkReleaseRequests, setIsBulkReleaseRequests] = useState(false);
  const debouncedSearch = useDebounce(search, 300);
  const [requestsStatus, setRequestsStatus] = useState(REQUESTS_STATUS.PENDING);
  const selectedBranches = useRecoilValue(sharedAtoms.selectedBranches);

  // Set by whoever opened the modal on a specific request (the scheduling board's pending chips).
  // Reset on unmount so the next opener - e.g. the header icon - always starts on the full list.
  const [requestsFilter, setRequestsFilter] = useRecoilState(sharedAtoms.requestsModalFilter);
  const filteredRequestIds = requestsFilter?.requestIds;
  const isFiltered = !!filteredRequestIds?.length;
  const clearRequestsFilter = useCallback(() => setRequestsFilter(null), [setRequestsFilter]);

  // unmount-only: recoil setters are referentially stable, so this never fires mid-life
  useEffect(() => () => setRequestsFilter(null), [setRequestsFilter]);

  const { admins, mappedAdmins, isLoading: isLoadingAdmins } = useAdmins();

  // ALL_BOOKERS = "All bookers"; undefined = not yet defaulted
  const [selectedBookerId, setSelectedBookerId] = useState(undefined);
  const isBookerInitialized = selectedBookerId !== undefined;

  useEffect(() => {
    // wait until  admins have settled before defaulting,
    if (isBookerInitialized || isLoadingAdmins) {
      return;
    }
    // default "All bookers"
    setSelectedBookerId(ALL_BOOKERS);
  }, [isBookerInitialized, isLoadingAdmins, mappedAdmins]);

  const bookerOptions = useMemo(
    () => [
      { label: t('allBookers'), value: ALL_BOOKERS },
      ...admins.map((admin) => ({
        label: [admin.firstName, admin.lastName].filter(Boolean).join(' '),
        value: admin.id
      }))
    ],
    [admins, t]
  );

  const {
    isLoading: loadingRequests,
    requests,
    updateRequestStatus,
    updateBulkRequestStatuses,
    refetch,
    page,
    pageSize,
    setPage,
    total: totalRequests
  } = useRequests({
    // While filtered, ignore the branch selection: `selectedBranches` is a global atom shared with
    // the rest of the app, so a narrower selection made earlier in the session would hide the very
    // request the user just clicked - and the branch picker is hidden here, leaving no way to
    // recover. Falling back to null makes useRequests send every branch the admin can see, which the
    // backend intersects with their own scope anyway.
    selectedBranches: isFiltered ? null : selectedBranches,
    search: debouncedSearch,
    requestsStatus,
    bookerId: typeof selectedBookerId === 'number' ? selectedBookerId : undefined,
    requestIds: filteredRequestIds,
    enabled: isBookerInitialized
  });

  const [selectedShiftSwap, setSelectedShiftSwap] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);

  const checkboxData = useCheckbox({
    defaultChecked: [],
    disabled: (requests || [])
      .filter(
        ({ status, typeId }) =>
          status !== REQUEST_STATUS.PENDING ||
          !isRequestEligibleForApproval({
            enableContractFeature,
            enableContractLeaveManagementFeature,
            typeId,
            myPrivileges,
            organizationId
          })
      )
      .map(({ id }) => id),
    disabledTooltip: t('requestWasAlreadyModified')
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const absenceTypeId = useMemo(() => Object.values(mappedRequestsTypes).find(({ type }) => type === 'absence')?.id, [
    mappedRequestsTypes
  ]);

  const data = useMemo(
    () =>
      (requests || []).map((item) => ({
        ...item,
        subRows: [item]
      })),
    [requests]
  );

  // Who/what the active request filter points at, for the filter banner. Resolved off the fetched
  // row rather than passed in by the opener, so the banner cannot disagree with the list it labels.
  // Null while the rows load, and when the filtered request is absent from the current status tab
  // (e.g. it was just approved out of the pending list) - the banner then shows the count alone.
  const filterSummary = useMemo(() => {
    if (!isFiltered) {
      return null;
    }

    const request = (requests || []).find(({ id }) => filteredRequestIds.includes(id));

    if (!request) {
      return null;
    }

    const { firstName, lastName, typeId, data: requestData } = request;

    const typeName =
      typeId === RequestTypes.LEAVE_REQUEST
        ? mappedLeaveTypes?.[requestData?.accountLeaveTypeId]?.name
        : typeId === absenceTypeId
        ? mappedRequestsTypes?.[absenceTypeId]?.kinds?.[requestData?.jobTypeId]?.label
        : mappedRequestsTypes?.[typeId]?.type && t(mappedRequestsTypes[typeId].type);

    return {
      employeeName: [firstName, lastName].filter(Boolean).join(' '),
      typeName: typeName || ''
    };
  }, [isFiltered, filteredRequestIds, requests, mappedLeaveTypes, mappedRequestsTypes, absenceTypeId, t]);

  const isFilterSummaryLoading = isFiltered && !filterSummary && (loadingRequests || !isBookerInitialized);

  const checkedRequests = useMemo(() => {
    if (!isBulkReleaseRequests) return [];

    const value = checkboxData?.value;
    const disabled = new Set(checkboxData?.disabled || []);

    if (value?.all) {
      return data.filter(({ id }) => !disabled.has(id)).map(({ id }) => ({ id }));
    }

    if (value?.exclude) {
      return data.filter(({ id }) => !value.exclude.includes(id)).map(({ id }) => ({ id }));
    }

    return (value?.include || []).map((id) => ({ id }));
  }, [isBulkReleaseRequests, checkboxData?.value, checkboxData?.disabled, data]);

  const aggregateJobTypes = useMemo(() => {
    const types = groupBy(data, 'typeId');
    const jobTypes = groupBy(data, 'data.jobTypeId');

    const titleCounters = [];

    Object.entries(types).forEach(([id, list]) => {
      if (Number(id) === absenceTypeId) {
        Object.entries(jobTypes).forEach(([jobTypeId, allData]) => {
          if (jobTypeId && !!mappedRequestsTypes?.[1]?.kinds?.[jobTypeId]) {
            titleCounters.push({
              ...mappedRequestsTypes?.[1]?.kinds?.[jobTypeId],
              count: allData.length
            });
          }
        });
      } else {
        titleCounters.push({
          label:
            mappedRequestsTypes?.[id]?.type ||
            (Number(id) === RequestTypes.LEAVE_REQUEST ? RequestTypesSlugs.LEAVE_REQUEST : undefined),
          count: list.length,
          color: TYPES_COLROS[id]?.color
        });
      }
    });
    return titleCounters;
  }, [absenceTypeId, data, mappedRequestsTypes]);

  const onChangeStatus = useCallback(
    (updatedStatus) => {
      setRequestsStatus(updatedStatus);
      if (updatedStatus !== requestsStatus) {
        setIsBulkReleaseRequests(false);
        checkboxData?.toggleAll(null);
      }
    },
    [requestsStatus, checkboxData]
  );

  const title = useMemo(
    () => (
      <TitleRow>
        <Header2SemiBold>{t('requests')}</Header2SemiBold>

        {/* search / booker / status / branch all narrow the list, which is meaningless once it is
            already pinned to specific requests - the filter chip is the only control that applies */}
        {!isFiltered && (
          <HistorySelect>
            <Search value={search} onChange={setSearch} />

            <BookerSelectContainer>
              <SelectHeader
                value={selectedBookerId ?? ALL_BOOKERS}
                onChange={setSelectedBookerId}
                options={bookerOptions}
              />
            </BookerSelectContainer>

            <SelectHeader
              value={requestsStatus}
              onChange={onChangeStatus}
              options={[
                { label: t(REQUESTS_STATUS.PENDING), value: REQUESTS_STATUS.PENDING },
                { label: t(REQUESTS_STATUS.HISTORY), value: REQUESTS_STATUS.HISTORY }
              ]}
            />

            <BranchPickerContainer>
              <BranchPicker />
            </BranchPickerContainer>
          </HistorySelect>
        )}
      </TitleRow>
    ),
    [requestsStatus, search, t, onChangeStatus, selectedBookerId, bookerOptions, isFiltered]
  );

  const buildRequestDisplay = useCallback(
    (requestId) => {
      const request = (requests || []).find((current) => current.id === requestId);
      const { startDate, endDate, accountLeaveTypeId } = request?.data || {};
      const startMoment = moment(startDate, API_DATE_FORMAT);
      const endMoment = moment(endDate, API_DATE_FORMAT);
      const dateRange = startMoment.isSame(endMoment, 'day')
        ? startMoment.format(shortDateFormat)
        : [startMoment, endMoment]
            .filter((current) => current.isValid())
            .map((current) => current.format(shortDateFormat))
            .join(' - ');
      return {
        employeeName: [request?.firstName, request?.lastName].filter(Boolean).join(' '),
        firstName: request?.firstName || '',
        leaveTypeName: mappedLeaveTypes?.[accountLeaveTypeId]?.name || '',
        dateRange
      };
    },
    [requests, mappedLeaveTypes, shortDateFormat]
  );

  const {
    pendingNegative,
    pendingEarningsLimit,
    pendingUnbook,
    confirming,
    approveRequest,
    confirmNegative,
    confirmEarningsLimit,
    confirmUnbook,
    closeNegative,
    closeEarningsLimit,
    closeUnbook
  } = useLeaveRequestApproval({ updateRequestStatus, buildRequestDisplay });

  const onCloseRequestModalStickyBar = useCallback(() => {
    setIsBulkReleaseRequests(false);
    checkboxData?.toggleAll(null);
  }, [checkboxData]);

  const declineRequest = useCallback(
    ({ managerReason, requestIds, isBulk }) => {
      if (!requestIds?.length) return;

      isBulk && onCloseRequestModalStickyBar();
      return !isBulk
        ? updateRequestStatus({ accountId, requestId: requestIds[0], status: REQUEST_STATUS.DECLINED, managerReason })
        : updateBulkRequestStatuses({ accountId, requestIds, status: REQUEST_STATUS.DECLINED, managerReason });
    },
    [accountId, updateRequestStatus, updateBulkRequestStatuses, onCloseRequestModalStickyBar]
  );

  const handleNameClick = useCallback(
    (rowData) => {
      const requestKindsForContract = [3, 4]; // 3= Sick, 4= Holiday

      const eligibleForContractTab =
        enableContractFeature &&
        rowData?.typeId === absenceTypeId &&
        requestKindsForContract.includes(rowData?.data?.jobTypeId);

      const defaultActiveTab = eligibleForContractTab ? 'contracts' : 'profile';
      setSelectedEmployee({ id: rowData?.employeeId, defaultActiveTab });
    },
    [absenceTypeId, enableContractFeature, setSelectedEmployee]
  );

  const columns = useMemo(
    () => [
      {
        Header: t('status'),
        dataKey: 'status',
        Cell: (props) => (props.rowData.subRows ? <ActionRenderer {...props} /> : <></>),
        columnData: { approveRequest, setIsDeclineModalOpen, updateRequestStatus },
        maxWidth: 80,
        captureRowClick: false,
        frozen: !isBulkReleaseRequests
      },
      {
        Header: t('name'),
        dataKey: 'name',
        Cell: (props) =>
          props.rowData.subRows ? (
            <NameRenderer
              {...props}
              columnData={{
                onClick: () => {
                  handleNameClick(props.rowData);
                }
              }}
            />
          ) : (
            <></>
          ),
        frozen: !isBulkReleaseRequests
      },
      {
        Header: t('dateRequested'),
        dataKey: 'createdAt',
        Cell: (props) => (props.rowData.subRows ? <TimeAgoRenderer {...props} /> : <></>)
      },
      ...(requestsStatus === REQUESTS_STATUS.HISTORY
        ? [
            {
              Header: t('actionAt'),
              dataKey: 'actionAt',
              Cell: (props) => (props.rowData.subRows ? <TimeAgoRenderer {...props} /> : <></>)
            },
            {
              Header: t('actionBy'),
              dataKey: 'admin',
              formatter: (admin) =>
                admin ? <>{[admin.firstName, admin.lastName].filter((curr) => !!curr).join(' ')}</> : <></>
            }
          ]
        : []),
      {
        Header: t('type'),
        dataKey: 'type',
        minWidth: 120,
        Aggregate: TypeRenderer,
        Cell: TypeRendererDetails,
        columnData: { mappedRequestsTypes, mappedLeaveTypes },
        cellRendererProps: { style: { width: '100%' } }
      },
      {
        Header: t('when'),
        dataKey: 'when',
        Aggregate: WhenRenderer,
        Cell: WhenRendererDetails,
        columnData: { mappedRequestsTypesBySlug },
        minWidth: 150
      },
      ...(enableContractLeaveManagementFeature
        ? [
            {
              Header: t('hoursDaysRequestedColumn'),
              dataKey: 'hoursDaysRequested',
              formatter: (_, { data: rowData }) => formatHoursDaysRequested(t, rowData)
            }
          ]
        : []),
      {
        Header: t('position'),
        dataKey: 'positionId',
        formatter: (_, { data: { positionId } }) => (positionId ? mappedPositions?.[positionId]?.slug : '')
      },
      {
        Header: t('location'),
        dataKey: 'shiftLocationId',
        formatter: (_, { data: { shiftLocationId } }) =>
          shiftLocationId ? mappedLocations?.[shiftLocationId]?.name : ''
      },
      {
        Header: t('notes'),
        dataKey: 'employeeReason',
        Cell: NotesRenderer
      },

      {
        Header: t('shiftReleaseDetails'),
        dataKey: 'shiftReleaseDetails',
        formatter: (_, { typeId, data: { startTime, endTime } }) => {
          if (typeId !== RequestTypes.RELEASE_SHIFT) {
            return '';
          }

          return `${moment(startTime, API_DATE_TIME_FORMAT).format(shortDateFormat)}, ${[
            moment(startTime, API_DATE_TIME_FORMAT).format(timeFormat),
            endTime && moment(endTime, API_DATE_TIME_FORMAT).format(timeFormat)
          ].join('-')}`;
        }
      },

      {
        Header: t('details'),
        dataKey: 'details',
        Cell: DetailsRenderer,
        columnData: {
          setSelectedEmployee,
          mappedLabels,
          mappedLocations,
          mappedRequestsTypes,
          setSelectedShiftSwap
        }
      },
      {
        Header: t('branchName'),
        dataKey: 'branchId',
        Cell: ({ rowData: { branchId, data: requestData } }) => {
          const requestBranchId = branchId || requestData?.projectBranchId;
          return <>{requestBranchId ? mappedBranches?.[requestBranchId]?.name : ''}</>;
        }
      },
      {
        Header: t('eventBooker'),
        dataKey: 'bookerId',
        formatter: (_, { data: requestData }) => {
          const booker = requestData?.bookerId ? mappedAdmins?.[requestData.bookerId] : null;
          return booker ? [booker.firstName, booker.lastName].filter(Boolean).join(' ') : '';
        }
      }
    ],
    [
      approveRequest,
      enableContractLeaveManagementFeature,
      mappedAdmins,
      mappedBranches,
      mappedLabels,
      mappedLeaveTypes,
      mappedLocations,
      mappedPositions,
      mappedRequestsTypes,
      mappedRequestsTypesBySlug,
      requestsStatus,
      shortDateFormat,
      t,
      timeFormat,
      updateRequestStatus,
      handleNameClick,
      isBulkReleaseRequests
    ]
  );

  const onCloseSwap = useCallback(() => setSelectedShiftSwap(null), [setSelectedShiftSwap]);
  const onCloseEmployeeCard = useCallback(() => setSelectedEmployee(false), [setSelectedEmployee]);

  const handleApproveAllRequests = useCallback(async () => {
    await updateBulkRequestStatuses({
      accountId,
      requestIds: checkedRequests.map(({ id }) => id),
      status: REQUEST_STATUS.APPROVED
    });
    onCloseRequestModalStickyBar();
  }, [checkedRequests, updateBulkRequestStatuses, accountId, onCloseRequestModalStickyBar]);

  const contextMenuOptions = useMemo(
    () =>
      [
        {
          label: t('bulkReleaseRequests'),
          onClick: () => setIsBulkReleaseRequests(true),
          css: responsiveShow.tablet
        }
      ].map((item) =>
        isAccountNetworkAccessSupplier && !item.allowNonPremiumAccount
          ? {
              ...item,
              disabled: true,
              label: (
                <>
                  {item.label} <PremiumStartIcon />
                </>
              )
            }
          : item
      ),
    [isAccountNetworkAccessSupplier, t]
  );

  const onCloseDeclineRequestModal = () => setIsDeclineModalOpen(false);
  return (
    <Modal onClose={onClose} title={title} showConfirmButton={false} closeOnClickOutside={false} usePortal>
      {/* while filtered the banner replaces the sub-header outright: a per-type count across the
          whole list, and a bulk-release menu to select out of it, both say nothing about one request */}
      {isFiltered ? (
        <RequestsFilterBanner
          t={t}
          count={filteredRequestIds.length}
          summary={filterSummary}
          isLoading={isFilterSummaryLoading}
          onClear={clearRequestsFilter}
        />
      ) : (
        <SubHeaderWrapper>
          {!!aggregateJobTypes && !!aggregateJobTypes.length && (
            <Types>
              {aggregateJobTypes.map(({ label, color, count }, index) => (
                <FlexMiddle key={index}>
                  <Dot color={color} />
                  <SmallBodyBold>
                    {t(label)} ({count})
                  </SmallBodyBold>
                </FlexMiddle>
              ))}
            </Types>
          )}

          {requestsStatus === REQUESTS_STATUS.PENDING && !!data.length && <ContextMenu options={contextMenuOptions} />}
        </SubHeaderWrapper>
      )}
      <Table
        columns={columns}
        page={page}
        setPage={setPage}
        totalItems={totalRequests}
        checkboxData={isBulkReleaseRequests && checkboxData}
        pageSize={pageSize}
        resizable
        data={data}
        isLoading={loadingRequests || loadingRequestsType}
        shadow={false}
        noDataRenderer={() => <EmptyState />}
      />
      {isBulkReleaseRequests && (
        <RequestModalStickyBar
          checkedRequests={checkedRequests}
          totalRequests={data.length}
          onClose={onCloseRequestModalStickyBar}
          setIsDeclineModalOpen={setIsDeclineModalOpen}
          handleApproveAllRequests={handleApproveAllRequests}
        />
      )}

      {isDeclineModalOpen && (
        <RequestDeclineReasonModal
          {...isDeclineModalOpen}
          declineRequest={declineRequest}
          onClose={onCloseDeclineRequestModal}
        />
      )}

      {selectedEmployee && (
        <EmployeeCard
          defaultActiveTab={selectedEmployee.defaultActiveTab ?? 'generalAvailability'}
          editProfile
          defaultData={selectedEmployee}
          onClose={onCloseEmployeeCard}
        />
      )}

      {selectedShiftSwap && (
        <SwapSideBar
          data={selectedShiftSwap}
          onClose={onCloseSwap}
          onApprove={approveRequest}
          onDecline={declineRequest}
        />
      )}

      {pendingNegative && (
        <LeaveApprovalConfirmModal
          variant="negative"
          employeeName={pendingNegative.employeeName}
          firstName={pendingNegative.firstName}
          leaveTypeName={pendingNegative.leaveTypeName}
          balance={pendingNegative.balance}
          expectedBalance={pendingNegative.expectedBalance}
          balanceUnit={pendingNegative.balanceUnit}
          loading={confirming}
          onConfirm={confirmNegative}
          onClose={closeNegative}
        />
      )}

      {pendingEarningsLimit && (
        <LeaveEarningsLimitWarningModal
          loading={confirming}
          onConfirm={confirmEarningsLimit}
          onClose={closeEarningsLimit}
        />
      )}

      {pendingUnbook && (
        <LeaveApprovalConfirmModal
          variant="unbook"
          employeeName={pendingUnbook.employeeName}
          firstName={pendingUnbook.firstName}
          leaveTypeName={pendingUnbook.leaveTypeName}
          dateRange={pendingUnbook.dateRange}
          shifts={pendingUnbook.shifts}
          balance={pendingUnbook.balance}
          expectedBalance={pendingUnbook.expectedBalance}
          balanceUnit={pendingUnbook.balanceUnit}
          loading={confirming}
          onConfirm={confirmUnbook}
          onClose={closeUnbook}
        />
      )}
    </Modal>
  );
};

export default RequestsModal;
